import React, { useCallback, useState } from 'react';
import { AutoSizeTextarea } from './auto-size-textarea';
import { createUseStyles } from 'react-jss';
import { DynamicGridActions } from './dynamic-grid-actions/dynamic-grid-actions';
import { DynamicGridCell } from './dynamic-grid-cell';
import { DynamicGridHeaderActions } from './dynamic-grid-actions/dynamic-grid-header-actions';
import { DynamicGridHeaderCell } from './dynamic-grid-header-cell';
import { DynamicGridRow } from './dynamic-grid-row';
import colors from '@packages/core/styles/colors';

interface DynamicGridColumnType {
    worksheetElementGridId: string;
    columnLabel: string;
    columnOrder: number;
    defaultValue: string;
    subColumns?: never[];
    isRequired: boolean;
}

export interface DynamicGridRowType {
    rowLabel: string;
    rowOrder: number;
    subRows?: never[];
    columns: DynamicGridColumnType[];
}

export type DynamicGridChangeKey = 'corner' | 'column-label' | 'row-label' | 'cell';

export interface DynamicGridChangeData {
    value: string;
    rowOrder?: number;
    columnOrder?: number;
    worksheetElementGridId?: string;
}

interface DynamicGridBuilderProps {
    areAllColumnsRequired: boolean;
    cornerHeader: string;
    isDeleteColumnButtonDisabled: boolean;
    isDeleteRowButtonDisabled: boolean;
    onChange: (key: DynamicGridChangeKey, data: DynamicGridChangeData) => void;
    onDeleteColumn: (columnOrder: number) => void;
    onDeleteRow: (rowOrder: number) => void;
    onToggleRequired: () => void;
    rows: DynamicGridRowType[];
}

const useStyles = createUseStyles({
    container: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        overflowX: 'auto',
    },
    table: {
        height: '100%',
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
        borderSpacing: 0,
        minWidth: '100%',
    },
    hideActionsContainer: {
        flex: 1,
        width: '100%',
        overflow: 'auto',
        position: 'relative',
    },
    hideActionsTable: {
        width: '100%',
        height: '100%',
        tableLayout: 'auto',
        borderCollapse: 'collapse',
        borderSpacing: 0,
    },
    actionsRow: {
        minHeight: 20,
        height: 20,
        '& :not(:last-child)': {
            borderTop: `1px solid ${colors.grayThree}`,
        },
    },
    textWeightMedium: {
        fontWeight: 500,
    },
    actionsColumn: {
        width: 50,
        minWidth: 50,
        maxWidth: 50,
    },
    simpleCell: {
        border: `1px solid ${colors.grayThree}`,
    },
    cell: {
        borderTop: `1px solid ${colors.grayThree}`,
        borderLeft: `1px solid ${colors.grayThree}`,
        borderRight: 'none',
        minWidth: 90,
        width: 90,
    },
    lastCell: {
        borderRight: `1px solid ${colors.grayThree}`,
    },
    hoveredBorderLeft: {
        borderLeft: `1px solid ${colors.brandSecondary}`,
    },
    hoveredBorderRight: {
        borderRight: `1px solid ${colors.brandSecondary}`,
    },
    hoveredBorderTop: {
        borderTop: `1px solid ${colors.brandSecondary}`,
    },
    hoveredBorderBottom: {
        borderBottom: `1px solid ${colors.brandSecondary}`,
    },
});

export function DynamicGridBuilder({
    areAllColumnsRequired,
    cornerHeader = '',
    isDeleteColumnButtonDisabled,
    isDeleteRowButtonDisabled,
    onDeleteColumn,
    onDeleteRow,
    onChange,
    onToggleRequired,
    rows,
}: DynamicGridBuilderProps) {
    const classes = useStyles();
    const [hoveredRowOrder, setHoveredRowOrder] = useState<number | null>(null);
    const [hoveredColumnOrder, setHoveredColumnOrder] = useState<number | null>(null);
    const [showHeaderMenuPopover, setShowHeaderMenuPopover] = useState(false);

    const focusTextarea = useCallback((e: React.MouseEvent<HTMLTableCellElement>) => {
        const textarea = e.currentTarget.querySelector('textarea');
        if (textarea && !textarea.disabled) {
            textarea.focus();
        }
    }, []);

    function getCellClasses(
        columnOrder: number,
        rowOrder: number,
        isFirstRow: boolean,
        isLastRow: boolean,
        isFirstCol: boolean,
        isLastCol: boolean
    ): string {
        const classNames: string[] = [classes.cell];

        const isColHovered = hoveredColumnOrder === columnOrder;
        const isRowHovered = hoveredRowOrder === rowOrder;

        if (isColHovered) {
            classNames.push(classes.hoveredBorderLeft, classes.hoveredBorderRight);
            if (isFirstRow) classNames.push(classes.hoveredBorderTop);
            if (isLastRow) classNames.push(classes.hoveredBorderBottom);
        }

        if (isRowHovered) {
            classNames.push(classes.hoveredBorderTop, classes.hoveredBorderBottom);
            if (isFirstCol) classNames.push(classes.hoveredBorderLeft);
            if (isLastCol) classNames.push(classes.hoveredBorderRight);
        }

        if (!isColHovered && !isRowHovered && isLastCol) {
            classNames.push(classes.lastCell);
        }

        return classNames.join(' ');
    }

    return (
        <div className={classes.container}>
            <table className={classes.table}>
                <thead>
                    <DynamicGridRow>
                        <DynamicGridHeaderCell
                            className={`${getCellClasses(0, 0, true, false, true, false)} `}
                            onClick={focusTextarea}
                        >
                            <AutoSizeTextarea
                                id="corner-header"
                                isLabel={true}
                                className={classes.textWeightMedium}
                                onChange={(value) => onChange('corner', { value })}
                                value={cornerHeader}
                            />
                        </DynamicGridHeaderCell>
                        {rows[0].columns.map((column, columnIndex: number) => {
                            const isLastColumn = columnIndex === rows[0].columns.length - 1;
                            return (
                                <DynamicGridHeaderCell
                                    key={`col-header-${column.columnOrder}-${columnIndex}`}
                                    className={getCellClasses(column.columnOrder, 0, true, false, false, isLastColumn)}
                                    onClick={focusTextarea}
                                >
                                    <AutoSizeTextarea
                                        id={`column-label-${column.columnOrder}`}
                                        className={classes.textWeightMedium}
                                        isLabel={true}
                                        onChange={(value) =>
                                            onChange('column-label', { columnOrder: column.columnOrder, value })
                                        }
                                        required={column.isRequired}
                                        value={column.columnLabel}
                                    />
                                </DynamicGridHeaderCell>
                            );
                        })}
                        <DynamicGridHeaderActions
                            areAllColumnsRequired={areAllColumnsRequired ?? false}
                            onPopover={() => setShowHeaderMenuPopover(true)}
                            onToggleRequired={onToggleRequired ?? (() => {})}
                            setShowHeaderMenuPopover={setShowHeaderMenuPopover}
                            showPopover={showHeaderMenuPopover}
                        />
                    </DynamicGridRow>
                </thead>

                <tbody>
                    {rows.map((row, rowIndex) => {
                        const isLastRow = rowIndex === rows.length - 1;
                        const isRowActionsHovered = hoveredRowOrder === row.rowOrder;
                        return (
                            <DynamicGridRow key={`row-${row.rowOrder}-${rowIndex}`}>
                                <DynamicGridHeaderCell
                                    className={getCellClasses(0, row.rowOrder, false, isLastRow, true, false)}
                                    onClick={focusTextarea}
                                >
                                    <AutoSizeTextarea
                                        id={`row-label-${row.rowOrder}`}
                                        className={classes.textWeightMedium}
                                        value={row.rowLabel}
                                        onChange={(value) => onChange('row-label', { rowOrder: row.rowOrder, value })}
                                        isLabel={true}
                                    />
                                </DynamicGridHeaderCell>

                                {row.columns.map((column, columnIndex) => {
                                    const isLastColumn = columnIndex === rows[0].columns.length - 1;
                                    const id = `${column?.worksheetElementGridId ?? 'cell'}-${row.rowOrder}-${
                                        column.columnOrder
                                    }`;
                                    const cellKey =
                                        column?.worksheetElementGridId != null
                                            ? `cell-${column?.worksheetElementGridId}`
                                            : `cell-pos-${rowIndex}-${columnIndex}`;
                                    return (
                                        <DynamicGridCell
                                            key={cellKey}
                                            onClick={focusTextarea}
                                            className={getCellClasses(
                                                column.columnOrder,
                                                row.rowOrder,
                                                false,
                                                isLastRow,
                                                false,
                                                isLastColumn
                                            )}
                                        >
                                            <AutoSizeTextarea
                                                id={id}
                                                value={column.defaultValue}
                                                onChange={(value) =>
                                                    onChange('cell', {
                                                        worksheetElementGridId: column.worksheetElementGridId,
                                                        value,
                                                    })
                                                }
                                            />
                                        </DynamicGridCell>
                                    );
                                })}
                                <DynamicGridCell
                                    onMouseEnter={() => setHoveredRowOrder(row.rowOrder)}
                                    onMouseLeave={() => setHoveredRowOrder(null)}
                                    className={classes.actionsColumn}
                                    readOnly={true}
                                >
                                    <DynamicGridActions
                                        onDelete={() => onDeleteRow(row.rowOrder)}
                                        isDeleteDisabled={isDeleteRowButtonDisabled || showHeaderMenuPopover}
                                        showHeaderMenuPopover={showHeaderMenuPopover}
                                        isHovered={isRowActionsHovered}
                                    />
                                </DynamicGridCell>
                            </DynamicGridRow>
                        );
                    })}
                    <DynamicGridRow className={classes.actionsRow}>
                        <td aria-hidden="true" />
                        {rows[0].columns.map((column) => {
                            const isColActionsHovered = hoveredColumnOrder === column.columnOrder;

                            return (
                                <DynamicGridCell
                                    key={`col-action-${column.columnOrder}`}
                                    onMouseEnter={() => setHoveredColumnOrder(column.columnOrder)}
                                    onMouseLeave={() => setHoveredColumnOrder(null)}
                                    readOnly={true}
                                >
                                    <DynamicGridActions
                                        onDelete={() => onDeleteColumn(column.columnOrder)}
                                        isDeleteDisabled={isDeleteColumnButtonDisabled || showHeaderMenuPopover}
                                        showHeaderMenuPopover={showHeaderMenuPopover}
                                        isHovered={isColActionsHovered}
                                    />
                                </DynamicGridCell>
                            );
                        })}
                        <td aria-hidden="true" />
                    </DynamicGridRow>
                </tbody>
            </table>
        </div>
    );
}
